import React, { useState, useEffect } from 'react';
import {
  X,
  UploadCloud,
  CheckSquare,
  Square,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { UserProfile } from '../lib/firebase';
import { saveKundaliToCloud } from '../lib/kundaliService';

interface GuestChartMigrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetUser: UserProfile;
  onMigrationComplete?: (syncedCount: number, remainingCount: number) => void;
  lang?: string;
}

interface GuestChartItem {
  id: string;
  name: string;
  date: string;
  time: string;
  placeName: string;
  selected: boolean;
  rawKundali: any;
}

export const GuestChartMigrationModal: React.FC<GuestChartMigrationModalProps> = ({
  isOpen,
  onClose,
  targetUser,
  onMigrationComplete,
  lang = 'en',
}) => {
  const isHi = lang === 'hi';

  const [charts, setCharts] = useState<GuestChartItem[]>([]);
  const [migrating, setMigrating] = useState(false);
  const [migrationResults, setMigrationResults] = useState<{
    synced: number;
    failed: number;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const raw = localStorage.getItem('astroveda_saved_kundalis_guest');
    if (!raw) {
      setCharts([]);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const items: GuestChartItem[] = parsed.map((k: any, idx: number) => ({
          id: k.id || `guest_chart_${idx}_${Date.now()}`,
          name: k.birthDetails?.name || k.name || `Kundali #${idx + 1}`,
          date: k.birthDetails?.date || k.date || '',
          time: k.birthDetails?.time || k.time || '',
          placeName: k.birthDetails?.placeName || k.placeName || '',
          selected: true,
          rawKundali: k,
        }));
        setCharts(items);
      }
    } catch {
      setCharts([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleSelect = (id: string) => {
    setCharts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, selected: !c.selected } : c))
    );
  };

  const selectAll = (val: boolean) => {
    setCharts((prev) => prev.map((c) => ({ ...c, selected: val })));
  };

  const handleMigrate = async () => {
    const toMigrate = charts.filter((c) => c.selected);
    if (toMigrate.length === 0) return;

    setMigrating(true);
    let synced = 0;
    let failed = 0;

    const remainingGuestCharts: any[] = [];
    const userStorageKey = `astroveda_saved_kundalis_${targetUser.uid}`;
    let existingUserCharts: any[] = [];
    try {
      const userCached = localStorage.getItem(userStorageKey);
      if (userCached) existingUserCharts = JSON.parse(userCached);
    } catch {}

    // Process migration with Promise.allSettled for zero silent drops
    const migrationPromises = toMigrate.map(async (item) => {
      const stableId = (item.rawKundali.id && !item.rawKundali.id.startsWith('guest_') && !item.rawKundali.id.startsWith('knd_local_'))
        ? item.rawKundali.id
        : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `knd_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`);

      const migratedChart = {
        ...item.rawKundali,
        id: stableId,
        userId: targetUser.uid,
        userEmail: targetUser.email,
        tags: Array.isArray(item.rawKundali.tags)
          ? Array.from(new Set([...item.rawKundali.tags, 'Migrated', 'Guest Migration']))
          : ['Migrated', 'Guest Migration'],
        migratedAt: new Date().toISOString(),
      };

      // Try uploading to cloud Firestore
      try {
        await saveKundaliToCloud(
          migratedChart,
          targetUser,
          stableId,
          migratedChart.tags,
          migratedChart.notes || 'Migrated from unauthenticated guest session',
          false
        );
      } catch (cloudErr) {
        console.warn(`[MIGRATION CLOUD SAVE] Notice: Cloud save failed for ${item.name}, syncing to local user storage.`);
      }

      // Add to user storage
      existingUserCharts.push(migratedChart);
      return item.id;
    });

    const unselectedItems = charts.filter((c) => !c.selected).map((c) => c.rawKundali);
    const results = await Promise.allSettled(migrationPromises);

    results.forEach((res, index) => {
      if (res.status === 'fulfilled') {
        synced++;
      } else {
        failed++;
        remainingGuestCharts.push(toMigrate[index].rawKundali);
      }
    });

    // Add unselected charts back to guest storage
    const finalGuestList = [...remainingGuestCharts, ...unselectedItems];
    localStorage.setItem('astroveda_saved_kundalis_guest', JSON.stringify(finalGuestList));
    localStorage.setItem(userStorageKey, JSON.stringify(existingUserCharts));

    setMigrating(false);
    setMigrationResults({ synced, failed });

    if (onMigrationComplete) {
      onMigrationComplete(synced, finalGuestList.length);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-lg bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl p-6 text-slate-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
              <UploadCloud className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-serif font-bold text-amber-300">
                {isHi ? 'अतिथि कुंडलियों का स्थानांतरण' : 'Migrate Saved Guest Kundalis'}
              </h3>
              <p className="text-xs text-slate-400">
                {isHi
                  ? `खाता: ${targetUser.email}`
                  : `Target account: ${targetUser.email}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="mt-4">
          {migrationResults ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h4 className="text-base font-serif font-bold text-slate-100">
                {isHi ? 'स्थानांतरण पूर्ण!' : 'Migration Complete!'}
              </h4>
              <p className="text-xs text-slate-300 max-w-sm mx-auto">
                {isHi
                  ? `${migrationResults.synced} कुंडलियाँ आपके पंजीकृत खाते में स्थानांतरित कर दी गई हैं।`
                  : `Successfully migrated ${migrationResults.synced} Kundalis into your permanent account.`}
                {migrationResults.failed > 0 && (
                  <span className="block mt-1 text-amber-400">
                    {isHi
                      ? `${migrationResults.failed} कुंडलियाँ सुरक्षित रूप से स्थानीय संग्रह में बनी हुई हैं।`
                      : `${migrationResults.failed} charts remain safely stored in local guest cache for later retry.`}
                  </span>
                )}
              </p>
              <div className="pt-2">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 bg-gradient-to-r from-amber-600 to-amber-700 text-slate-950 font-semibold rounded-xl text-xs transition"
                >
                  {isHi ? 'प्रारंभ करें' : 'Proceed to Workspace'}
                </button>
              </div>
            </div>
          ) : charts.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400 space-y-2">
              <p>
                {isHi
                  ? 'स्थानांतरण हेतु कोई अतिथि कुंडली उपलब्ध नहीं है।'
                  : 'No local guest Kundalis found to migrate.'}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-slate-800 text-slate-300 rounded-xl text-xs hover:bg-slate-700 transition"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-slate-300">
                {isHi
                  ? 'अतिथि सत्र के दौरान निर्मित कुंडलियों का चयन करें जिन्हें आप अपने स्थायी खाते में सहेजना चाहते हैं:'
                  : 'Select the charts created during your guest exploration to associate with your permanent account:'}
              </p>

              {/* Selection controls */}
              <div className="flex items-center justify-between text-xs text-slate-400 pb-1">
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => selectAll(true)}
                    className="hover:text-amber-300 underline"
                  >
                    Select All
                  </button>
                  <span>•</span>
                  <button
                    type="button"
                    onClick={() => selectAll(false)}
                    className="hover:text-amber-300 underline"
                  >
                    Deselect All
                  </button>
                </div>
                <span>
                  {charts.filter((c) => c.selected).length}/{charts.length} selected
                </span>
              </div>

              {/* Chart List */}
              <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                {charts.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => toggleSelect(c.id)}
                    className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition text-xs ${
                      c.selected
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {c.selected ? (
                        <CheckSquare className="w-4 h-4 text-amber-400 shrink-0" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-600 shrink-0" />
                      )}
                      <div>
                        <div className="font-semibold text-slate-200">{c.name}</div>
                        <div className="text-[11px] text-slate-400">
                          {c.date} {c.time} {c.placeName ? `• ${c.placeName}` : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 transition"
                >
                  {isHi ? 'छोड़ें' : 'Skip Migration'}
                </button>
                <button
                  type="button"
                  onClick={handleMigrate}
                  disabled={migrating || charts.filter((c) => c.selected).length === 0}
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-slate-950 transition flex items-center space-x-1.5 disabled:opacity-50"
                >
                  {migrating ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Migrating...</span>
                    </>
                  ) : (
                    <span>
                      Migrate {charts.filter((c) => c.selected).length} Chart(s)
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
